import React, { useContext, useEffect, useState, useRef } from "react";
import { Table, Tag, Row, Col, Popover, Spin, Button, Dropdown, Checkbox} from "antd";
import { DownOutlined } from "@ant-design/icons";
import {
    BsBoxArrowUpRight,
    BsFillBoxSeamFill,
    BsFillStarFill,
} from "react-icons/bs";
import { StarFilled, LoadingOutlined } from "@ant-design/icons";
import { LuTrash } from "react-icons/lu";
import { MdRefresh } from "react-icons/md";
import { titleCase, capitalizeWords, getSortedChannelList } from "@ts/utils";
import { CommonWidgetMetrics, Platforms, RevstackDashboardModules, VariantsCellRendererProps, BCCCitySelection, BCCSelectionLevel, BCCSelectionMode, TSelectedDateRange, ICitySelectionDropdownProps, BCC_CITY_DISABLED_REASONS } from "@ts/common-types";
import { useDispatch } from "react-redux";
import { updateProductPriority, toggleVariantSelected, toggleProductDisabled } from "@/features/clustersSlice";
import { ReactComponent as PriorityIcon } from "@images/priority.svg";
import { formatMetric } from "@ts/revstack/helpers";
import { openPageInNewTab } from "@ts/revstack/filters";
import { getCompetitionSOVData } from "@ts/retrieve-data";
import DeltaTag from "./DeltaTag";
import NoDataText from "./NoDataText";
import { campaignCreationContext, REVSTACK_MEASURES } from "@ts/constants";
import { showToast } from "@/ts/modals";
import { REVSTACK_THEME_COLORS } from "@/ts/constants";
import { IoIosAddCircleOutline,  IoIosRemoveCircleOutline } from "react-icons/io";
import { updateSelectedChangeSku } from "@/features/modalSlice";
import store from "@/store";
import { useCitySelectionState } from '@hooks/AllCustomHooks';
import { findCityById, getSearchPlaceholder } from '@ts/bcc-city-selection-utils';
import {
    SelectionTag,
    SelectableItem,
    SelectAllItem,
    ZoneItem,
    Breadcrumb,
    SearchBar,
    LevelSelector,
    ModeToggle,
    CohortLevelSelector,
    CohortPreview,
    Footer,
} from './CitySelectionDropdownComponents';

// Global render queue using functional approach
const createTableRenderQueue = () => {
    const queue: Array<{ id: string; resolve: () => void }> = [];
    let activeRenders = 0;
    const maxConcurrent = 2; // Allow 2 tables to render simultaneously

    const requestRender = async (id: string): Promise<void> => {
        if (activeRenders < maxConcurrent) {
            activeRenders++;
            return Promise.resolve();
        }

        return new Promise((resolve) => {
            queue.push({ id, resolve });
        });
    };

    const completeRender = (id: string): void => {
        activeRenders = Math.max(0, activeRenders - 1);
        if (queue.length > 0) {
            const next = queue.shift();
            if (next) {
                activeRenders++;
                setTimeout(() => next.resolve(), 5); // Minimal delay for responsiveness
            }
        }
    };

    const getQueuePosition = (id: string): number => {
        const index = queue.findIndex(item => item.id === id);
        return index >= 0 ? index + 1 : 0;
    };

    return {
        requestRender,
        completeRender,
        getQueuePosition
    };
};

type KeywordCellProps = { text: string; record: any };

export const TableTitleCellRenderer = (title: string, textColor = "secondary-blue") => {
    return (
        <span
            className={`fw-semibold lh-sm text-uppercase d-block fs-8 text-${textColor}`}
            style={{ letterSpacing: 0.6, paddingLeft: 12 }}
        >
            {title}
        </span>
    );
};

export const ChangeCellRenderer = (
    metric: CommonWidgetMetrics,
    value: number,
    change: any,
    deltaPosition: "right" | "bottom" = "right",
    isPositive?: boolean,
    justifyContent: "center" | "start" | "end" = "center",
    changeUnit?: string,
    decimalPlaces?: number
) => {
    return (
        <div
            className={`${deltaPosition === "right" ? "d-flex" : "d-block"
                } align-items-center justify-content-${justifyContent} gap-2`}
        >
            <div>
                <span
                    className="fs-7 fw-500"
                    style={{
                        color: "#1e1b39",
                    }}
                >
                    {formatMetric(metric, value, false, decimalPlaces)}
                </span>
            </div>
            <DeltaTag
                metric={metric}
                value={change}
                unit={changeUnit}
                className="fs-8 fw-500"
                isPositive={isPositive || change ? change >= 0 : null}
            />
        </div>
    );
};

const AnomaliesPopoverContent = ({ anomalies }: { anomalies: any[] }) => (
    <div
        style={{
            background: "#1E1B39",
            borderRadius: 10,
            minWidth: 280,
            color: "#fff",

            padding: 0,
            overflow: "hidden",
            fontSize: 12,
        }}
    >
        {anomalies.map((section: any, idx: number) => (
            <div key={section.title}>
                <div
                    className="fw-600"
                    style={{
                        color: "#919191",
                        letterSpacing: 1,
                        textTransform: "uppercase",
                        borderBottom: "1px solid #2B2841",
                        padding: "10px 20px",
                    }}
                >
                    {section.title}
                </div>
                {section.metrics.map((item: any, i: number) => (
                    <div
                        className="d-flex align-items-center gap-2 fw-400"
                        key={item.name + i}
                        style={{
                            padding: "8px 20px",
                            borderBottom:
                                i === section.metrics.length - 1 &&
                                    idx === anomalies.length - 1
                                    ? "none"
                                    : "1px solid #2B2841",
                        }}
                    >
                        <span style={{ flex: 2 }}>{item.name}</span>
                        <span style={{ flex: 1, textAlign: "right" }}>
                            {item.value}
                        </span>
                        <span
                            className="d-flex align-items-center justify-content-start fw-500"
                            style={{
                                flex: 1,
                                color: item.isPositive ? "#00C48C" : "#F04438",
                            }}
                        >
                            <span>{typeof item.isPositive === "boolean" ? item.isPositive ? "▲" : "▼" : ""}</span>
                            <span className="ms-2">{item.change || "-"}</span>
                        </span>
                    </div>
                ))}
            </div>
        ))}
    </div>
);

const AnomaliesTag = ({ anomalies }: { anomalies: any[] }) => {
    if (anomalies?.length === 0) return "-";

    const count = anomalies?.length - 1;
    const title = anomalies?.[0]?.title || "";
    const isTitleLong = title?.length > 10;
    const displayTitle = isTitleLong ? title.slice(0, 10) + "..." : title;

    const getTagWidth = () => {
        const baseWidth = isTitleLong ? 10 : title?.length;
        const padding = 2;
        const badgeWidth = count > 0 ? 3 : 0;
        return `${baseWidth + padding + badgeWidth}ch`;
    };

    return (
        <Popover
            placement="top"
            styles={{ body: { padding: 0, borderRadius: 14 } }}
            content={<AnomaliesPopoverContent anomalies={anomalies} />}
            trigger="hover"
        >
            <Tag
                role="button"
                bordered={false}
                style={{
                    background: "#e5f8ff",
                    color: "#1e1b39",
                    borderRadius: 6,
                    fontWeight: 600,
                    fontSize: 12,
                    padding: "0 10px",
                    display: "inline-flex",
                    alignItems: "center",
                    height: 26,
                    width: getTagWidth(),
                }}
            >
                {displayTitle}
                {count > 0 && (
                    <span
                        className="d-flex align-items-center justify-content-center ms-2 rounded-circle bg-white text-black"
                        style={{
                            fontWeight: 700,
                            fontSize: 10,
                            width: 18,
                            height: 18,
                        }}
                    >
                        +{count}
                    </span>
                )}
            </Tag>
        </Popover>
    );
};

export const TableCellRenderer = (
    metric: CommonWidgetMetrics,
    label: string,
    value: string | number,
    change?: number,
    isPositive?: boolean
) => {
    return (
        <Row
            align="middle"
            style={{ width: "100%", fontSize: 14, color: "#1e1b39" }}
            gutter={4}
        >
            <Col flex="2 1 0%" style={{ textAlign: "left" }}>
                {label}
            </Col>
            <Col flex="1 1 0%" style={{ textAlign: "right" }}>
                {formatMetric(metric, value)}
            </Col>
            <Col flex="1 1 0%" style={{ textAlign: "left" }}>
                {change !== undefined ? (
                    <DeltaTag
                        metric={metric}
                        value={change}
                        style={{ fontSize: 12 }}
                        isPositive={isPositive || change ? change >= 0 : null}
                    />
                ) : (
                    <span></span>
                )}
            </Col>
        </Row>
    );
};

export const ExpandableRow = ({
    expandedColumns,
    record,
    showExpandTableHeader = true,
    isExpandedColsSameAsParent = false,
    scrollX = "max-content"
}: {
    expandedColumns: any;
    record: any;
    showExpandTableHeader?: boolean;
    isExpandedColsSameAsParent?: boolean;
    scrollX?: string|number;
}) => {
   const renderQueue = createTableRenderQueue();
    const [renderState, setRenderState] = React.useState<'loading' | 'queued' | 'rendering' | 'full'>('loading');
    const componentId = React.useRef<string>(`table-${Date.now()}-${Math.random()}`);
    const renderRequestRef = React.useRef<boolean>(false);
    const containerRef = React.useRef<HTMLDivElement>(null);
    
    // IMPORTANT: All hooks must be called before any conditional returns
    // Always show full data as originally intended
    const dataToShow = React.useMemo(() => {
        if (!record.drilldown || record.drilldown.length === 0) {
            return [];
        }
        return record.drilldown;
    }, [record.drilldown]);


    // Simplified rendering logic - always progress from loading state
    useEffect(() => {
        if (renderState === 'loading' && !renderRequestRef.current) {
            renderRequestRef.current = true;
            
            // Always use queue system to render full table (restore original behavior)
            setRenderState('queued');
            
            // Request permission to render
            renderQueue.requestRender(componentId.current).then(() => {
                setRenderState('rendering');
                
                setTimeout(() => {
                    setRenderState('full');
                    setTimeout(() => {
                        window.dispatchEvent(new CustomEvent('expandedTableRendered'));
                        renderQueue.completeRender(componentId.current);
                    }, 50);
                }, 25);
            });
            
            // Update queue position periodically
            const updatePosition = () => {
                const position = renderQueue.getQueuePosition(componentId.current);
                if (position > 0) {
                    setTimeout(updatePosition, 100);
                }
            };
            updatePosition();
        }
    }, [renderState]);

    useEffect(() => {
        return () => {
            renderQueue.completeRender(componentId.current);
        };
    }, []);

    // Now safe to have conditional returns after all hooks are called
    const hasData = record.drilldown && record.drilldown.length > 0;
    if (!hasData) {
        return (
            <div
                className="d-flex align-items-center justify-content-center"
                style={{ height: 40 }}
            >
                <NoDataText />
            </div>
        );
    }

    if (renderState === 'loading' || renderState === 'queued' || renderState === 'rendering') {
        return (
            <div
                ref={containerRef}
                className="d-flex align-items-center justify-content-center"
                style={{ 
                    height: 40,
                    background: isExpandedColsSameAsParent ? "transparent" : "#F8FBFD",
                    border: "none",
                    fontSize: "12px",
                    color: "#999"
                }}
            >
                {`Loading ${record.drilldown.length} items...`}
            </div>
        );
    }

    return (
        <Table
            className={`revstack-expandable-table bg-transparent ${
                isExpandedColsSameAsParent ? "hide-scrollbar" : ""
            }`}
            columns={expandedColumns}
            dataSource={dataToShow}
            pagination={false}
            rowKey={(row, idx) => idx}
            size="small"
            showHeader={showExpandTableHeader}
            scroll={{
                x: scrollX,
            }}
            //!Note: Don't remove expandable, we are using this to set for styling the columns and rows
            expandable={
                isExpandedColsSameAsParent
                    ? {
                          showExpandColumn: true,
                          expandIconColumnIndex: 0,
                          expandIcon: () => <></>,
                          expandedRowRender: () => <></>,
                          columnWidth: 50,
                      }
                    : {}
            }
            style={
                isExpandedColsSameAsParent
                    ? {}
                    : {
                          background: isExpandedColsSameAsParent
                              ? "transparent"
                              : "#F8FBFD",
                          boxShadow: "none",
                          border: "none",
                          overflow: "hidden",
                      }
            }
            components={{
                body: {
                    row: (props: any) => (
                        <tr
                            {...props}
                            style={{
                                ...props.style,
                                height: isExpandedColsSameAsParent
                                    ? "60px"
                                    : "40px",
                                background: isExpandedColsSameAsParent
                                    ? "transparent"
                                    : "#F8FBFD",
                                textAlign: "right",
                            }}
                        />
                    ),
                },
            }}
        />
    );
};

export const CategoryTagCellRenderer = ({
    value,
    hasBackground = false,
}: {
    value: string;
    hasBackground?: boolean;
}) => (
    <div
        className="d-flex align-items-center justify-content-between"
        style={{ width: "fit-content" }}
    >
        {hasBackground ? (
            <Tag
                className="d-flex align-items-center justify-content-center fs-8 fw-600 px-1"
                bordered={true}
                style={{
                    color: "#2D2D2D",
                    backgroundColor: "#F0F4F8",
                    borderColor: "#CBD9E7",
                    borderRadius: 3,
                    height: 19,
                    flexShrink: 0,
                }}
            >
                {value?.length > 30
                    ? capitalizeWords(value?.slice(0, 30)) + "..."
                    : capitalizeWords(value)}
            </Tag>
        ) : (
            <span className="fs-8 fw-600" style={{ color: "#6B7280" }}>
                {value?.length > 30
                    ? capitalizeWords(value?.slice(0, 30)) + "..."
                    : capitalizeWords(value)}
            </span>
        )}
    </div>
);

export const AnomaliesCellRenderer = (anomalies: any[]) => {
    return <AnomaliesTag anomalies={anomalies} />;
};

export const CityCellRenderer = (value: string) => {
    if (!value) return '-';

    let cities: string[] = [];
    if (typeof value === 'string') {
        cities = value.split(',').map(city => city.trim()).filter(city => city);
    } else if (Array.isArray(value)) {
        cities = value;
    }

    if (cities.length === 0) return '-';
    if (cities.length === 1) return cities[0];

    const firstCity = cities[0];
    const otherCount = cities.length - 1;

    const popoverContent = (
        <div style={{ maxWidth: '250px' }}>
            <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
                Selected Cities:
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                {cities.map((city, index) => (
                    <div
                        key={index}
                        style={{
                            padding: '4px 8px',
                            backgroundColor: '#f5f5f5',
                            borderRadius: '4px',
                            fontSize: '12px'
                        }}
                    >
                        {city}
                    </div>
                ))}
            </div>
        </div>
    );

    return (
        <Popover
            content={popoverContent}
            placement="top"
            trigger="hover"
            overlayStyle={{ zIndex: 1050 }}
        >
            <span style={{ cursor: 'pointer' }}>
                {firstCity} + {otherCount} other{otherCount > 1 ? 's' : ''}
            </span>
        </Popover>
    );
};

export const ProductPopoverContent = ({
    title,
    isPowerSku = false,
}: {
    title: string;
    isPowerSku: boolean;
}) => (
    <div
        className="py-2 px-3 text-white overflow-hidden rounded-2"
        style={{
            background: "#1E1B39",
            maxWidth: 250,

            fontSize: 12,
        }}
    >
        {isPowerSku && (
            <div
                className="d-flex align-items-center gap-2 mb-2"
                style={{ color: "#B3E9FF", fontSize: 10 }}
            >
                <StarFilled />
                <span>Power SKU</span>
            </div>
        )}
        <div>{titleCase(title)}</div>
    </div>
);

//TODO: Explore if this can be made part of the config
const KeywordPopoverContent = ({
    compSovData = [],
    loading = false,
}: {
    compSovData?: any[];
    loading?: boolean;
}) => {
    const dataToDisplay = compSovData
        .sort((a, b) => b.paidSov - a.paidSov)
        .slice(0, 5);

    return (
        <div
            style={{
                background: "#1E1B39",
                borderRadius: 10,
                minWidth: 420,
                color: "#fff",
                padding: 0,
                overflow: "hidden",
                fontSize: 12,
            }}
        >
            <div
                className="fw-600 d-flex"
                style={{
                    color: "#919191",
                    letterSpacing: 1,
                    textTransform: "uppercase",
                    borderBottom: "1px solid #2B2841",
                    padding: "10px 20px",
                }}
            >
                <span style={{ flex: 2 }}>BRAND</span>
                <span style={{ flex: 3, textAlign: "center" }}>{REVSTACK_MEASURES[CommonWidgetMetrics.OVERALL_SOV].label}</span>
                <span style={{ flex: 3, textAlign: "center" }}>{REVSTACK_MEASURES[CommonWidgetMetrics.ORGANIC_SOV].label}</span>
                <span style={{ flex: 3, textAlign: "center" }}>{REVSTACK_MEASURES[CommonWidgetMetrics.PAID_SOV].label}</span>
            </div>
            {loading && (
                <div
                    className="d-flex align-items-center justify-content-center py-4"
                    style={{ minHeight: 96 }}
                >
                    <Spin
                        indicator={
                            <LoadingOutlined
                                style={{ fontSize: 18, color: "#B4B4B3" }}
                                spin
                            />
                        }
                        tip={
                            <span style={{ color: "#B4B4B3" }}>Loading...</span>
                        }
                    />
                </div>
            )}
            {!loading &&
                dataToDisplay.map((item, i) => (
                    <div
                        key={item.name}
                        className="d-flex align-items-center gap-2 fw-400"
                        style={{
                            padding: "10px 20px",
                            borderBottom:
                                i === dataToDisplay.length - 1
                                    ? "none"
                                    : "1px solid #2B2841",
                        }}
                    >
                        <span style={{ flex: 2 }}>
                            {capitalizeWords(item.name)}
                        </span>
                        <span style={{ flex: 3, textAlign: "center" }}>
                            <span className="me-2">
                                {formatMetric(
                                    CommonWidgetMetrics.OVERALL_SOV,
                                    item.overallSov
                                )}
                            </span>
                            <DeltaTag
                                metric={CommonWidgetMetrics.OVERALL_SOV}
                                value={item.overallChange}
                                isPositive={item.overallChange ? item.overallChange >= 0 : null}
                                className="fs-8 fw-500"
                            />
                        </span>
                        <span style={{ flex: 3, textAlign: "center" }}>
                            <span className="me-2">
                                {formatMetric(
                                    CommonWidgetMetrics.ORGANIC_SOV,
                                    item.organicSov
                                )}
                            </span>
                            <DeltaTag
                                metric={CommonWidgetMetrics.ORGANIC_SOV}
                                value={item.organicChange}
                                isPositive={item.organicChange ? item.organicChange >= 0 : null}
                                className="fs-8 fw-500"
                            />
                        </span>
                        <span style={{ flex: 3, textAlign: "center" }}>
                            <span className="me-2">
                                {formatMetric(
                                    CommonWidgetMetrics.PAID_SOV,
                                    item.paidSov
                                )}
                            </span>
                            <DeltaTag
                                metric={CommonWidgetMetrics.PAID_SOV}
                                value={item.paidChange}
                                isPositive={item.paidChange ? item.paidChange >= 0 : null}
                                className="fs-8 fw-500"
                            />
                        </span>
                    </div>
                ))}
        </div>
    );
};

export const ProductCellRenderer = (
    text: string,
    record: any,
    {
        isImageRequired = false,
        isLinkRequired = false,
        size = "small",
        titleCharLimit = 22,
    }: { isImageRequired?: boolean; isLinkRequired?: boolean; size?: "small" | "medium" | "large"; titleCharLimit?: number } = {}
) => {

    const finalTitleCharLimit = size === "large" ? 40 : size === "medium" ? 28 : titleCharLimit || 22;
    return (
        <div className="product-cell-wrapper w-100 d-flex justify-content-between px-2">
            <div className="d-flex align-items-center justify-content-start gap-2" style={{ paddingLeft: 14 }}>
            {isImageRequired && (
                <div
                    className="d-flex align-items-center justify-content-center rounded position-relative p-1"
                    style={{
                        width: size === "large" ? 68 : size === "medium" ? 46 : 34,
                        height: size === "large" ? 74 : size === "medium" ? 51 : 37,
                        background: record.thumbnail ? "#fff" : "#f0f0f0",
                        border: record.thumbnail ? "1px solid #f0f0f0" : "none",
                        flexShrink: 0,
                    }}
                >
                    {record?.thumbnail ? (
                        <img
                            src={record.thumbnail}
                            alt={text}
                            className="w-100 h-100"
                            style={{ objectFit: "contain" }}
                        />
                    ) : (
                        <BsFillBoxSeamFill
                            size={32}
                            style={{ color: "#bdbdbd" }}
                        />
                    )}
                    {record?.isPowerSku && (
                        <BsFillStarFill
                            size={10}
                            className="position-absolute top-0 end-0"
                            style={{ fill: "#EFBE10" }}
                        />
                    )}
                </div>
            )}
            <div className="d-flex flex-column justify-content-between">
                <div
                    className="d-flex align-items-center justify-content-between gap-2 fw-semibold fs-6"
                    style={{ color: "#0078AA", cursor: `${record?.sku ? "pointer" : "default"}` }}
                >
                    <Popover
                        placement="top"
                        styles={{ body: { padding: 0, borderRadius: 14 } }}
                        content={
                            <ProductPopoverContent
                                title={text}
                                isPowerSku={record?.isPowerSku}
                            />
                        }
                        trigger="hover"
                    >
                        <span
                            className="fs-7 fw-600"
                            onClick={() => {
                                if (record?.sku) {

                                    const mainFilters = store.getState().mainFilters.value;
                                    openPageInNewTab({
                                        platform: Platforms.PLANNING,
                                        module: RevstackDashboardModules.PRODUCT_ANALYSIS,
                                        type:"filters",
                                        queryParams: {
                                            sku: record?.sku,
                                            client: store.getState()
                                                .activeClientId.value,
                                            channel:
                                                store.getState().revstackChannel
                                                    .value,
                                            mainFilters,        
                                            dateRange:
                                                store.getState()
                                                    .selectedDateRange.value as TSelectedDateRange,
                                            advancedFilters: {},
                                        },
                                        mainFilters,
                                    });
                                }
                            }}
                        >
                            {text?.length > finalTitleCharLimit
                                ? titleCase(text?.slice(0, titleCharLimit)) + "..."
                                : titleCase(text)}
                        </span>
                    </Popover>
                </div>
                <div className="d-flex align-items-center justify-content-start gap-1">
                {record?.brand && (
                    <CategoryTagCellRenderer
                        value={record.brand}
                        hasBackground={true}
                    />
                )}
                {record?.category && (
                    <CategoryTagCellRenderer
                        value={record.category}
                        hasBackground={false}
                    />
                )}
                </div>
            </div>
        </div>
        <div className={`${size === "medium" ? "pt-1" : ""}`}>
        {isLinkRequired && (
                        <BsBoxArrowUpRight
                            role="button"
                            style={{ color: "#919191" }}
                            size={14}
                            onClick={() => {
                                const urlToOpen = record?.product_url || "";
                                window.open(urlToOpen, "_blank");
                            }}
                        />
                    )}
        </div>
        </div>
    );
};

export const PivotColumnCellRenderer = ({
    pivotKey,
    pivotSubKey,
}: {
    pivotKey: string;
    pivotSubKey?: string;
}) => {
    return (
        <div className="d-flex align-items-start justify-content-start" style={{ paddingLeft: 14 }}>
            <div>
                <div className="fw-semibold fs-6" style={{ color: "#0078AA" }}>
                    {pivotKey?.length > 20
                        ? titleCase(pivotKey?.slice(0, 20)) + "..."
                        : titleCase(pivotKey)}
                </div>
                {pivotSubKey && (
                    <div className="mt-1">
                        <CategoryTagCellRenderer
                            value={pivotSubKey}
                            hasBackground={false}
                        />
                    </div>
                )}
            </div>
        </div>
    );
}

const KeywordCell: React.FC<KeywordCellProps> = ({ text, record }) => {
    const [compSovData, setCompSovData] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    return (
        <div className="d-flex align-items-center justify-content-start" style={{ paddingLeft: 14 }}>
            <div>
                <span
                    className="d-flex align-items-center justify-content-between gap-2 fw-semibold fs-6"
                    style={{ color: "#0078AA" }}
                >
                    <Popover
                        placement="top"
                        styles={{ body: { padding: 0, borderRadius: 14 } }}
                        content={
                            <KeywordPopoverContent
                                compSovData={compSovData}
                                loading={isLoading}
                            />
                        }
                        trigger="hover"
                        mouseEnterDelay={0.2}
                        mouseLeaveDelay={0.1}
                        onOpenChange={(visible) => {
                            if (visible) {
                                setIsLoading(true);
                                getCompetitionSOVData(text)
                                    .then((data) => {
                                        setCompSovData(data);
                                    })
                                    .catch((error) => {
                                        console.error("Error fetching competition SOV data:", error);
                                    })
                                    .finally(() => setIsLoading(false));
                            } else {
                                setCompSovData([]);
                                setIsLoading(false);
                            }
                        }}
                    >
                        <span>
                            {text?.length > 20
                                ? titleCase(text?.slice(0, 20)) + "..."
                                : titleCase(text)}
                        </span>
                    </Popover>
                </span>
                {record?.brand && (
                    <CategoryTagCellRenderer
                        value={record.brand}
                        hasBackground={true}
                    />
                )}
                {record?.subcategory && (
                    <CategoryTagCellRenderer
                        value={record.subcategory}
                        hasBackground={false}
                    />
                )}
            </div>
        </div>
    );
};

export const KeywordCellRenderer = (text: string, record: any) => {
    return <KeywordCell text={text} record={record} />;
};

export const CrossChannelProductCellWrapper = (
    text: string,
    record: any,
    {
        size = "small",
        titleCharLimit = 22,
    }: {
        size?: "small" | "medium" | "large";
        titleCharLimit?: number
    } = {}
) => {
    const sortedChannels = getSortedChannelList();

    const firstHierarchyBasedChannel = sortedChannels
        .map((channel: any) => record[channel.id])
        .find((channelData: any) => channelData);

    const enhancedRecord = firstHierarchyBasedChannel
        ? {
            ...record,
            category: firstHierarchyBasedChannel.category,
            brand: firstHierarchyBasedChannel.brand,
            sku_title: firstHierarchyBasedChannel.sku_title,
            thumbnail: firstHierarchyBasedChannel.thumbnail,
            product_url: firstHierarchyBasedChannel.product_url,
        }
        : record;

    return ProductCellRenderer(
        enhancedRecord.sku_title,
        enhancedRecord, {
        isImageRequired: true,
        isLinkRequired: true,
        size: size,
        titleCharLimit: titleCharLimit,
    });
}

export const VariantsCellRenderer: React.FC<VariantsCellRendererProps> = ({
    value,
    record
}) => {
    const { isModalVisible, currentStep, selectedProducts } =
        useContext(campaignCreationContext);
    const dispatch = useDispatch();

    /* Build Product Keys */
    const productId = record?.id || "";
    const clusterId = record?.clusterId || (productId ? `##productCluster_${productId}` : "default");
    const variantKey = `${clusterId}_${productId}`;

    /* Initialize Variant State (only once per product) */
    const initializeVariantState = () => {
        if (initialVariantState[variantKey] || variantSelectionState[variantKey]) return;

        initialVariantState[variantKey] = {};
        variantSelectionState[variantKey] = {};

        const skuDetails = record?.skuDetails;

        // Case A: DB skuDetails exist
        if (Array.isArray(skuDetails) && skuDetails.length > 0) {
            skuDetails.forEach((skuDetail) => {
                const label = skuDetail?.unit || skuDetail?.sku || "";
                if (!label) return;

                const dbValue = skuDetail?.isActive ?? false;
                const isActive =
                    skuDetail?.isActiveForSession !== undefined
                        ? skuDetail.isActiveForSession
                        : dbValue;

                initialVariantState[variantKey][label] = dbValue;
                variantSelectionState[variantKey][label] = isActive;
            });
        }
        // Case B: Fallback to value array
        else if (Array.isArray(value) && value.length > 0) {
            value.forEach((label: string) => {
                if (!label) return;
                initialVariantState[variantKey][label] = false;
                variantSelectionState[variantKey][label] = false;
            });
        }
    };

    initializeVariantState();

    const isSelected = (label: string) =>
        getVariantSelectionFromState(clusterId, productId, label);

    /* Row Disabled Logic */
    const enforceSelection = isModalVisible && currentStep === 0;
    const isRowSelected = enforceSelection
        ? (selectedProducts || []).some((row) => String(row?.id) === String(record?.id))
        : true;
    const isDisabled = record?.disabled || !isRowSelected;

    const handleToggle = (label: string) => {
        if (isDisabled) return;

        const currentlySelected = isSelected(label);

        setVariantSelectionInState(clusterId, productId, label, !currentlySelected);

        dispatch(
            toggleVariantSelected({
                clusterId,
                productId,
                variantLabel: label
            })
        );

        // Global event for forcing updates in sibling renderers
        window.dispatchEvent(new CustomEvent("variantSelectionUpdated"));
    };

    const [, forceUpdate] = React.useReducer(x => x + 1, 0);

    /* Sync From skuDetails (prevent virtualization overwrite) */
    const lastSyncedRef = React.useRef<string>("");
    const hasInitializedRef = React.useRef<boolean>(false);

    React.useEffect(() => {
        const skuDetails = record?.skuDetails;

        if (!Array.isArray(skuDetails)) return;

        const currentState = JSON.stringify(
            skuDetails.map((s) => ({
                unit: s.unit,
                sku: s.sku,
                isActiveForSession: s.isActiveForSession,
                isActive: s.isActive
            }))
        );

        const shouldSync =
            currentState !== lastSyncedRef.current && !hasInitializedRef.current;

        if (shouldSync) {
            const existingSelections = variantSelectionState[variantKey];
            const hasExisting = existingSelections && Object.keys(existingSelections).length > 0;

            if (!hasExisting) {
                skuDetails.forEach((skuDetail) => {
                    const label = skuDetail?.unit || skuDetail?.sku || "";
                    if (!label) return;

                    const isActive =
                        skuDetail?.isActiveForSession !== undefined
                            ? skuDetail.isActiveForSession
                            : skuDetail?.isActive ?? false;

                    setVariantSelectionInState(clusterId, productId, label, isActive);
                });

                lastSyncedRef.current = currentState;
                forceUpdate();
            }
        }

        if (!hasInitializedRef.current) {
            hasInitializedRef.current = true;
        }
    }, [record?.skuDetails, clusterId, productId, variantKey]);

    React.useEffect(() => {
        const handler = () => forceUpdate();
        window.addEventListener("variantSelectionUpdated", handler);
        return () => window.removeEventListener("variantSelectionUpdated", handler);
    }, []);

    if (!value || value.length === 0) return <>-</>;

    return (
        <div
            className="d-flex align-items-center justify-content-start flex-wrap"
            style={{ gap: "8px" }}
        >
            {value.map((item, index) => {
                const selected = isSelected(item);

                return (
                    <Tag
                        key={index}
                        onClick={() => handleToggle(item)}
                        style={{
                            backgroundColor: selected
                                ? "#0078aa"
                                : isDisabled
                                ? "#f5f5f5"
                                : "#f0f8ff",
                            border: `1px solid ${isDisabled ? "#d9d9d9" : "#0078aa"}`,
                            color: selected
                                ? "#fff"
                                : isDisabled
                                ? "#bfbfbf"
                                : "#0078aa",
                            borderRadius: "20px",
                            padding: "2px 8px",
                            fontSize: "10px",
                            fontWeight: 500,
                            margin: 0,
                            borderColor: isDisabled ? "#d9d9d9" : "#0078aa",
                            height: "20px",
                            lineHeight: "16px",
                            cursor: isDisabled ? "not-allowed" : "pointer",
                            userSelect: "none",
                            position: "relative"
                        }}
                    >
                        {item}
                    </Tag>
                );
            })}
        </div>
    );
};

const initialPriorityState: { [key: string]: number } = {};

// Store modified priorities (local state)
const priorityState: { [key: string]: number } = {};

// Track next available priority per cluster
const nextPriorityByCluster: { [clusterId: string]: number } = {};

// Store initial variant state from DB (similar to initialPriorityState)
const initialVariantState: { [key: string]: { [variantLabel: string]: boolean } } = {};

// Store variant selection state (local state)
const variantSelectionState: { [key: string]: { [variantLabel: string]: boolean } } = {};

export const getProductPriorityFromState = (clusterId: string, productId: string): number => {
    const priorityKey = `${clusterId}_${productId}`;
    return priorityState[priorityKey] || 0;
};

export const getVariantSelectionFromState = (clusterId: string, productId: string, variantLabel: string): boolean => {
    const key = `${clusterId}_${productId}`;
    return variantSelectionState[key]?.[variantLabel] || false;
};

export const setVariantSelectionInState = (clusterId: string, productId: string, variantLabel: string, selected: boolean) => {
    const key = `${clusterId}_${productId}`;
    if (!variantSelectionState[key]) {
        variantSelectionState[key] = {};
    }
    variantSelectionState[key][variantLabel] = selected;
};

export const getInitialVariantSelectionFromState = (clusterId: string, productId: string, variantLabel: string): boolean => {
    const key = `${clusterId}_${productId}`;
    return initialVariantState[key]?.[variantLabel] || false;
};

export const setInitialVariantSelectionInState = (clusterId: string, productId: string, variantLabel: string, selected: boolean) => {
    const key = `${clusterId}_${productId}`;
    if (!initialVariantState[key]) {
        initialVariantState[key] = {};
    }
    initialVariantState[key][variantLabel] = selected;
};

export const resetAllModalVariantSelections = () => {
    Object.keys(variantSelectionState).forEach(key => {
        if (key.startsWith('##productCluster_')) {
            delete variantSelectionState[key];
        }
    });
};

export const resetClusterVariantSelections = (clusterId: string) => {
    // Reset variant selection state to initial DB values from initialVariantState
    Object.keys(initialVariantState).forEach(key => {
        if (key.startsWith(`${clusterId}_`)) {
            // Reset to initial DB values
            variantSelectionState[key] = { ...initialVariantState[key] };
        }
    });

    // Force re-render of variant cells
    window.dispatchEvent(new CustomEvent('variantSelectionUpdated'));
};

export const resetProductVariantSelections = (clusterId: string, productIds: string[]) => {
    if (!Array.isArray(productIds) || productIds.length === 0) return;

    // Reset deselected products' variant selections to initial DB values
    productIds.forEach((productId) => {
        const key = `${clusterId}_${productId}`;
        if (initialVariantState[key]) {
            // Reset to initial DB values
            variantSelectionState[key] = { ...initialVariantState[key] };
        } else {
            // If no initial state exists, clear the variant selection state
            variantSelectionState[key] = {};
        }
    });

    // Force re-render of variant cells
    window.dispatchEvent(new CustomEvent('variantSelectionUpdated'));
};

// Reset priority state for a specific cluster to use original database values
export const resetClusterPriorityState = (clusterId: string) => {
    // Reset priority state to original DB values from initialPriorityState
    Object.keys(initialPriorityState).forEach(key => {
        if (key.startsWith(`${clusterId}_`)) {
            // Restore original DB value, or set to 0 if no original value exists
            priorityState[key] = initialPriorityState[key] || 0;
        }
    });

    // Reset next priority counter for this cluster
    delete nextPriorityByCluster[clusterId];

    // Force re-render of priority cells
    window.dispatchEvent(new CustomEvent('priorityUpdated'));
};

// Reset priority for a list of products within a cluster and compact remaining priorities
export const resetProductPriorities = (clusterId: string, productIds: string[]) => {
    if (!Array.isArray(productIds) || productIds.length === 0) return;

    // Set deselected products' priorities to 0 in local state
    productIds.forEach((productId) => {
        const key = `${clusterId}_${productId}`;
        priorityState[key] = 0;
    });

    // Recompute compacted priorities for remaining products in this cluster
    const clusterEntries = Object.entries(priorityState)
        .filter(([key, value]) => key.startsWith(`${clusterId}_`) && (value as number) > 0)
        .sort((a, b) => (a[1] as number) - (b[1] as number));

    let next = 1;
    clusterEntries.forEach(([key]) => {
        priorityState[key] = next++;
    });

    // Update nextPriority counter
    nextPriorityByCluster[clusterId] = next;

    // Notify listeners to re-render priority cells
    window.dispatchEvent(new CustomEvent('priorityUpdated'));
};

export const PriorityCellRenderer = (value: any, record: any) => {
    const {
        isModalVisible,
        currentStep,
        selectedProducts,
    } = React.useContext(campaignCreationContext) as any;
    const dispatch = useDispatch();
    const productId = record.id || record.sku || 'default';
    const clusterId = record.clusterId || 'default';

    // Create a unique key combining cluster and product
    const priorityKey = `${clusterId}_${productId}`;

    // Initialize from record.priority_value (original DB value) if it exists and initial state is not set
    const initialPriority = record.priority_value;
    if (initialPriority !== undefined && initialPriority !== null && !initialPriorityState[priorityKey]) {
        // Store the original DB priority
        initialPriorityState[priorityKey] = initialPriority;

        // Initialize local state with the original DB value
        if (!(priorityKey in priorityState)) {
            priorityState[priorityKey] = initialPriority;
        }
    }

    // If local state has never been set (not even to 0) but we have initial state, use that
    if (!(priorityKey in priorityState) && initialPriorityState[priorityKey]) {
        priorityState[priorityKey] = initialPriorityState[priorityKey];
    }
    
    // Initialize or update next priority for this cluster
    if (!nextPriorityByCluster[clusterId]) {
        // Find the highest priority in this cluster's local state
        const clusterPriorities = Object.entries(priorityState)
            .filter(([key]) => key.startsWith(`${clusterId}_`))
            .map(([, value]) => value);
        const maxPriority = clusterPriorities.length > 0 ? Math.max(...clusterPriorities) : 0;
        nextPriorityByCluster[clusterId] = maxPriority + 1;
    }
    
    // Ensure nextPriority is always higher than any existing priority in the cluster
    const currentPriority = priorityState[priorityKey] || 0;
    if (currentPriority >= nextPriorityByCluster[clusterId]) {
        nextPriorityByCluster[clusterId] = currentPriority + 1;
    }
    
    const priority = priorityState[priorityKey] || 0;
    const isSet = priority > 0;

    // Enforce selection only in CampaignCreationModal step 0 (cluster selection)
    const enforceSelection = isModalVisible && currentStep === 0;
    const isSelected = enforceSelection
        ? (selectedProducts || []).some((row: any) => String(row?.id) === String(record?.id))
        : true;
    const isDisabled = record.disabled || !isSelected;
    
    const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
        e.stopPropagation(); // Prevent row selection

        // Don't allow priority changes for disabled products
        if (isDisabled) {
            return;
        }

        const oldPriority = priority;
        const action = priority === 0 ? 'SETTING' : 'REMOVING';
        let newPriorityValue;

        if (priority === 0) {
            // Check if next priority would exceed max (3)
            if (nextPriorityByCluster[clusterId] > 3) {
                showToast("warning", "You can only select priority upto 3 products");
                return;
            }
            // Assign next priority for this cluster
            newPriorityValue = nextPriorityByCluster[clusterId]++;
            priorityState[priorityKey] = newPriorityValue;
        } else {
            // Remove priority in local state
            newPriorityValue = 0;
            priorityState[priorityKey] = 0;

            // Get all priorities for this cluster from local state
            const clusterPriorities = Object.entries(priorityState)
                .filter(([key, value]) => key.startsWith(`${clusterId}_`) && value > 0)
                .sort((a, b) => a[1] - b[1]); // Sort by priority value

            // Reassign priorities sequentially in local state and Redux
            let sequentialPriority = 1;
            clusterPriorities.forEach(([key]) => {
                if (key !== priorityKey) {
                    const [clusterIdFromKey, productIdFromKey] = key.split('_');
                    priorityState[key] = sequentialPriority;

                    // Dispatch Redux action for each reassigned priority
                    dispatch(updateProductPriority({
                        clusterId: clusterIdFromKey,
                        productId: productIdFromKey,
                        priority: sequentialPriority
                    }));

                    sequentialPriority++;
                }
            });

            // Update next priority
            nextPriorityByCluster[clusterId] = sequentialPriority;
        }

        // Dispatch Redux action to update the priority in the store
        dispatch(updateProductPriority({
            clusterId,
            productId,
            priority: newPriorityValue
        }));

        // Force re-render by updating a global counter
        window.dispatchEvent(new CustomEvent('priorityUpdated'));
    };
    
    return (
        <div 
            className="d-flex align-items-center justify-content-center" 
            style={{
                height: '32px',
                cursor: isDisabled ? 'not-allowed' : 'pointer',
                opacity: isDisabled ? 0.5 : 1
            }}
            onClick={handleClick}
            role="button"
        >
            <div
                style={{
                    width: '24px',
                    height: '24px',
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.2s ease',
                    borderRadius: '4px'
                }}
            >
                <PriorityIcon
                    width={36}
                    height={36}
                    style={{
                        color: isDisabled ? '#D0D0D0' : (isSet ? '#FFEEB0' : '#C0D0E0'),
                        fill: isDisabled ? 'transparent' : (isSet ? '#FFEEB0' : 'transparent'),
                        stroke: isDisabled ? '#D0D0D0' : (isSet ? '#FFEEB0' : '#C0D0E0'),
                        strokeWidth: 1,
                        transition: 'all 0.2s ease',
                        opacity: 1
                    }}
                />
                {isSet && !isDisabled && (
                    <span
                        style={{
                            position: 'absolute',
                            fontSize: '14px',
                            fontWeight: '600',
                            color: '#2d2d2d',
                            lineHeight: '1',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            pointerEvents: 'none',
                            zIndex: 1
                        }}
                    >
                        {priority}
                    </span>
                )}
            </div>
        </div>
    );
}


export const ActionCellRenderer = (value: any, record: any, clusterId: string) => {
    const dispatch = useDispatch();
    const isDisabled = record.disabled || false;
    const productId = record.id || record.sku;
    
    // Use the same cluster-scoped key as in PriorityCellRenderer
    const priorityKey = `${clusterId}_${productId}`;
    
    const handleActionClick = (e: React.MouseEvent) => {
        e.stopPropagation(); // Prevent row selection
        
        // If product is being disabled and has a priority, clear it
        if (!isDisabled && priorityState[priorityKey] && priorityState[priorityKey] > 0) {
            const removedPriority = priorityState[priorityKey];
            priorityState[priorityKey] = 0;

            // Dispatch Redux action to clear priority in store
            dispatch(updateProductPriority({
                clusterId,
                productId,
                priority: 0
            }));

            // Get all priorities for this cluster from local state
            const clusterPriorities = Object.entries(priorityState)
                .filter(([key, value]) => key.startsWith(`${clusterId}_`) && value > 0)
                .sort((a, b) => a[1] - b[1]); // Sort by priority value

            // Reassign priorities sequentially in local state and Redux store
            let newPriority = 1;
            clusterPriorities.forEach(([key]) => {
                if (key !== priorityKey) {
                    priorityState[key] = newPriority;

                    // Extract productId from key (format: clusterId_productId)
                    const productIdFromKey = key.split('_')[1];

                    // Dispatch Redux action for each reassigned priority
                    dispatch(updateProductPriority({
                        clusterId,
                        productId: productIdFromKey,
                        priority: newPriority
                    }));

                    newPriority++;
                }
            });
            
            // Update next priority
            nextPriorityByCluster[clusterId] = newPriority;
            
            // Force re-render of priority cells
            window.dispatchEvent(new CustomEvent('priorityUpdated'));
        }
        
        dispatch(toggleProductDisabled({ 
            clusterId, 
            productId 
        }));
    };
    
    return (
        <div 
            className="d-flex align-items-center justify-content-center" 
            style={{
                height: '32px',
                cursor: 'pointer',
            }}
        >
            <Button
                size="small"
                onClick={handleActionClick}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: '24px',
                    height: '24px'
                }}
                shape="circle"
            >
                {isDisabled ? (
                    <MdRefresh
                        size={16}
                        style={{
                            color: '#0078AA',
                            transition: 'all 0.2s ease'
                        }}
                    />
                ) : (
                    <LuTrash
                        size={16}
                        style={{
                            color: '#EB7659',
                            transition: 'all 0.2s ease'
                        }}
                    />
                )}
            </Button>
        </div>
    );
}

export const tableAddSkuCellRenderer = (
    record: any,
    selectedChangeSku?: string | null
) => {
    const dispatch = useDispatch();
    const isSelected = selectedChangeSku === record.sku;
    return (
        <div className="d-flex align-items-center justify-content-center">
            <Button
                type="primary"
                icon={
                    isSelected ? (
                        <IoIosRemoveCircleOutline size={18} className="mt-1" />
                    ) : (
                        <IoIosAddCircleOutline size={18} className="mt-1" />
                    )
                }
                iconPosition="start"
                onClick={() => {
                    dispatch(
                        updateSelectedChangeSku(!isSelected ? record.sku : null)
                    );
                }}
                style={{
                    backgroundColor: isSelected
                        ? REVSTACK_THEME_COLORS.primary[700]
                        : "white",
                    borderColor: `${REVSTACK_THEME_COLORS.primary[700]}`,
                    fontSize: "14px",
                    color: isSelected
                        ? "white"
                        : `${REVSTACK_THEME_COLORS.primary[700]}`,
                }}
            >
                {isSelected ? "Selected" : "Select"}
            </Button>
        </div>
    );
};

export const CampaignCreationProductCellRenderer = (
    text: string,
    record: any,
    {
        isImageRequired = false,
        isLinkRequired = false,
    }: { isImageRequired?: boolean; isLinkRequired?: boolean } = {}
) => {

    return (
        <div className="d-flex align-items-center justify-content-start gap-2" style={{ paddingLeft: 14 }}>
            {isImageRequired && (
                <div
                    className="d-flex align-items-center justify-content-center rounded position-relative p-1"
                    style={{
                        width: 34,
                        height: 37,
                        background: record.thumbnail ? "#fff" : "#f0f0f0",
                        border: record.thumbnail ? "1px solid #f0f0f0" : "none",
                        flexShrink: 0,
                    }}
                >
                    {record?.thumbnail ? (
                        <img
                            src={record.thumbnail}
                            alt={text}
                            className="w-100 h-100"
                            style={{ objectFit: "contain" }}
                        />
                    ) : (
                        <BsFillBoxSeamFill
                            size={32}
                            style={{ color: "#bdbdbd" }}
                        />
                    )}
                    {record?.isPowerSku && (
                        <BsFillStarFill
                            size={10}
                            className="position-absolute top-0 end-0"
                            style={{ fill: "#EFBE10" }}
                        />
                    )}
                </div>
            )}
            <div className="d-flex flex-column justify-content-between">
                <div
                    className="d-flex align-items-center justify-content-between gap-2 fw-semibold fs-6"
                    style={{ color: "#0078AA" }}
                >
                    <span className="fs-7 fw-600" title={text} style={{
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: '24ch',
                    }}>
                        {titleCase(text)}
                    </span>
                    {isLinkRequired && (
                        <BsBoxArrowUpRight
                            role="button"
                            style={{ color: "#919191" }}
                            size={14}
                            onClick={() => {
                                const urlToOpen = record?.product_url || "";
                                window.open(urlToOpen, "_blank");
                            }}
                        />
                    )}
                </div>
                <div className="d-flex align-items-center justify-content-start gap-1">
                {record?.category !== '-' && (
                    <CategoryTagCellRenderer
                        value={record.category}
                        hasBackground={true}
                    />
                )}
                {record?.subcategory !== '-' && (
                    <CategoryTagCellRenderer
                        value={record.subcategory}
                        hasBackground={false}
                    />
                )}
                </div>
            </div>
        </div>
    );
};


export const CitySelectionDropdownWithCohortCellRenderer: React.FC<ICitySelectionDropdownProps> = ({
    value,
    record,
    citiesList,
    handleFieldChange,
}) => {
    // Use the custom hook for all state management
    const {
        ui,
        state,
        setters,
        handlers,
        individual,
        cohort,
        selectAll,
        checks,
        filters,
        selectors,
        allSelected,
        utils,
    } = useCitySelectionState({ value, citiesList, record, handleFieldChange });

    // Ref for tracking Apply button clicks
    const isApplyingRef = useRef(false);

    // Handle level change in individual mode
    const handleLevelChange = (level: BCCSelectionLevel) => {
        setters.setIndividualLevel(level);
        setters.setSearchQuery('');
        if (level !== BCCSelectionLevel.CUSTOM) {
            setters.setSelectedCustomZone(null);
        }
    };

    // Get the currently visible list in individual mode
    const getCurrentFilteredItems = () => {
        switch (ui.individualLevel) {
            case BCCSelectionLevel.CITY:
                return filters.getCities();
            case BCCSelectionLevel.REGION:
                return filters.getRegions();
            case BCCSelectionLevel.TIER:
                return filters.getTiers();
            case BCCSelectionLevel.CUSTOM:
                if (!ui.selectedCustomZone) {
                    return filters.getZones();
                }
                return filters.getCitiesInZone(ui.selectedCustomZone);
            default:
                return [];
        }
    };

    // Check if no results found
    const hasNoResults = !!ui.searchQuery.trim() && getCurrentFilteredItems().length === 0;

    // Generic render function for items with select all
    const renderItemsWithSelectAll = <T,>({
        selectableItems,
        filteredItems,
        allSelected: allItemsSelected,
        getItemId,
        getItemLabel,
        isSelected,
        isDisabled,
        getDisabledReason,
        onToggle,
        onSelectAll,
    }: {
        selectableItems: T[];
        filteredItems: T[];
        allSelected: boolean;
        getItemId: (item: T) => string;
        getItemLabel: (item: T) => string;
        isSelected: (item: T) => boolean;
        isDisabled: (item: T) => boolean;
        getDisabledReason?: (item: T) => string;
        onToggle: (item: T) => void;
        onSelectAll: () => void;
    }) => (
        <>
            {selectableItems.length > 0 && (
                <SelectAllItem
                    count={selectableItems.length}
                    allSelected={allItemsSelected}
                    someSelected={selectableItems.some((item) => isSelected(item))}
                    onToggle={onSelectAll}
                />
            )}
            {filteredItems.map((item) => {
                const id = getItemId(item);
                return (
                    <SelectableItem
                        key={id}
                        id={id}
                        label={getItemLabel(item)}
                        selected={isSelected(item)}
                        disabled={isDisabled(item)}
                        disabledReason={getDisabledReason?.(item)}
                        onToggle={() => onToggle(item)}
                    />
                );
            })}
        </>
    );

    // Render city items for individual mode
    const renderCityItems = () => {
        const selectableCities = selectors.getCities();
        const filteredCities = filters.getCities();

        return renderItemsWithSelectAll({
            selectableItems: selectableCities,
            filteredItems: filteredCities,
            allSelected: allSelected.cities(),
            getItemId: (city) => utils.getCityIdFn(city),
            getItemLabel: (city) => city.city_name,
            isSelected: (city) => checks.citySelected(utils.getCityIdFn(city)),
            isDisabled: (city) => checks.cityInCohort(utils.getCityIdFn(city)),
            getDisabledReason: () => BCC_CITY_DISABLED_REASONS.IN_COHORT,
            onToggle: individual.toggleCitySelection,
            onSelectAll: selectAll.toggleCities,
        });
    };

    // Render region items for individual mode
    const renderRegionItems = () => {
        const selectableRegions = selectors.getRegions();
        const filteredRegions = filters.getRegions();

        return renderItemsWithSelectAll({
            selectableItems: selectableRegions,
            filteredItems: filteredRegions,
            allSelected: allSelected.regions(),
            getItemId: (region) => region,
            getItemLabel: (region) => region,
            isSelected: (region) => checks.regionSelected(region),
            isDisabled: (region) => checks.regionInCohort(region),
            getDisabledReason: () => BCC_CITY_DISABLED_REASONS.IN_COHORT,
            onToggle: (region) => individual.toggleRegionSelection(region),
            onSelectAll: selectAll.toggleRegions,
        });
    };

    // Render tier items for individual mode
    const renderTierItems = () => {
        const selectableTiers = selectors.getTiers();
        const filteredTiers = filters.getTiers();

        return renderItemsWithSelectAll({
            selectableItems: selectableTiers,
            filteredItems: filteredTiers,
            allSelected: allSelected.tiers(),
            getItemId: (tier) => tier,
            getItemLabel: (tier) => tier,
            isSelected: (tier) => checks.tierSelected(tier),
            isDisabled: (tier) => checks.tierInCohort(tier),
            getDisabledReason: () => BCC_CITY_DISABLED_REASONS.IN_COHORT,
            onToggle: (tier) => individual.toggleTierSelection(tier),
            onSelectAll: selectAll.toggleTiers,
        });
    };

    // Render zone items for custom level
    const renderZoneItems = () => (
        <>
            {filters.getZones().map((zoneName) => {
                const selected = checks.zoneSelected(zoneName);
                const inCohort = checks.zoneInCohort(zoneName);

                return (
                    <ZoneItem
                        key={zoneName}
                        zoneName={zoneName}
                        selected={selected}
                        disabled={inCohort}
                        onToggle={(e) => {
                            e.stopPropagation();
                            !inCohort && individual.toggleZoneSelection(zoneName);
                        }}
                        onExpand={() => {
                            setters.setSelectedCustomZone(zoneName);
                            setters.setSearchQuery('');
                        }}
                    />
                );
            })}
        </>
    );

    // Render cities within a zone for custom level
    const renderCitiesInZone = () => {
        if (!ui.selectedCustomZone) return null;

        const cities = filters.getCitiesInZone(ui.selectedCustomZone);
        return (
            <>
                {cities.map((city) => {
                    const cityId = utils.getCityIdFn(city);
                    const selected = checks.citySelected(cityId);
                    const inZoneSelection = checks.cityInZoneSelection(cityId);
                    const inCohort = checks.cityInCohort(cityId);
                    const isDisabled = inZoneSelection || inCohort;
                    const disabledReason = inZoneSelection
                        ? BCC_CITY_DISABLED_REASONS.IN_ZONE
                        : inCohort
                        ? BCC_CITY_DISABLED_REASONS.IN_COHORT
                        : '';

                    return (
                        <SelectableItem
                            key={cityId}
                            id={cityId}
                            label={city.city_name}
                            selected={selected || inZoneSelection || inCohort}
                            disabled={isDisabled}
                            disabledReason={disabledReason}
                            onToggle={() => individual.toggleCustomZoneCitySelection(city)}
                        />
                    );
                })}
            </>
        );
    };

    // Individual Mode - 2 column layout: Level selection > Items list
    const renderIndividualMode = () => {
        const renderItemsByLevel = () => {
            switch (ui.individualLevel) {
                case BCCSelectionLevel.CITY:
                    return renderCityItems();
                case BCCSelectionLevel.REGION:
                    return renderRegionItems();
                case BCCSelectionLevel.TIER:
                    return renderTierItems();
                case BCCSelectionLevel.CUSTOM:
                    return ui.selectedCustomZone ? renderCitiesInZone() : renderZoneItems();
                default:
                    return null;
            }
        };

        return (
            <div className="city-selection-dropdown__individual">
                <LevelSelector
                    currentLevel={ui.individualLevel}
                    onLevelChange={handleLevelChange}
                />
                <div className="city-selection-dropdown__items-column">
                    {ui.individualLevel === BCCSelectionLevel.CUSTOM && ui.selectedCustomZone && (
                        <Breadcrumb
                            currentZone={ui.selectedCustomZone}
                            onBack={() => {
                                setters.setSelectedCustomZone(null);
                                setters.setSearchQuery('');
                            }}
                        />
                    )}
                    <SearchBar
                        placeholder={getSearchPlaceholder(ui.individualLevel, ui.selectedCustomZone)}
                        value={ui.searchQuery}
                        onChange={setters.setSearchQuery}
                    />
                    <div className="city-selection-dropdown__items-list">
                        {renderItemsByLevel()}
                        {hasNoResults && (
                            <div className="city-selection-dropdown__no-results">
                                No results found
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    // Get cohort item label
    const getCohortItemLabel = (itemId: string): string => {
        if (ui.selectedLevel === BCCSelectionLevel.CITY) {
            const city = findCityById(citiesList, itemId);
            return city?.city_name || itemId;
        }
        return itemId;
    };

    // Get individual selection status for cohort mode
    const getIndividualSelectionStatus = (itemId: string): boolean => {
        switch (ui.selectedLevel) {
            case BCCSelectionLevel.CITY: return checks.citySelected(itemId);
            case BCCSelectionLevel.REGION: return checks.regionSelected(itemId);
            case BCCSelectionLevel.TIER: return checks.tierSelected(itemId);
            case BCCSelectionLevel.CUSTOM: return checks.zoneSelected(itemId);
            default: return false;
        }
    };

    // Cohort Mode
    const renderCohortMode = () => (
        <div className="city-selection-dropdown__cohort">
            <CohortLevelSelector
                selectedLevel={ui.selectedLevel}
                onLevelChange={(level: BCCSelectionLevel) => {
                    setters.setSelectedLevel(level);
                    setters.setCohortBuilder([]);
                }}
            />
            <CohortPreview
                items={state.cohortBuilder}
                selectedLevel={ui.selectedLevel}
                citiesList={citiesList}
                onRemove={cohort.toggleItem}
                getCohortItemLabel={getCohortItemLabel}
            />

            {/* Items grid */}
            <div className="city-selection-dropdown__cohort-items-container">
                {/* Select All for Cohort Mode */}
                {selectors.getCohortItems().length > 0 && (
                    <div
                        className={`city-selection-dropdown__cohort-item city-selection-dropdown__cohort-item--select-all ${allSelected.cohortItems() ? 'city-selection-dropdown__cohort-item--selected' : ''}`}
                        style={{ marginBottom: '8px', paddingBottom: '8px', borderBottom: '1px solid #f0f0f0' }}
                    >
                        <Checkbox
                            checked={allSelected.cohortItems()}
                            indeterminate={selectors.getCohortItems().some(item => state.cohortBuilder.includes(item.id)) && !allSelected.cohortItems()}
                            onChange={selectAll.toggleCohortItems}
                        />
                        <span
                            className="city-selection-dropdown__cohort-item-label city-selection-dropdown__cohort-item-label--select-all"
                            onClick={selectAll.toggleCohortItems}
                            style={{ fontWeight: 500 }}
                        >
                            Select All ({selectors.getCohortItems().length})
                        </span>
                    </div>
                )}
                <div className="city-selection-dropdown__cohort-items-grid">
                    {cohort.getItems().map((item) => {
                        const isInBuilder = state.cohortBuilder.includes(item.id);
                        const isIndividuallySelected = getIndividualSelectionStatus(item.id);
                        const isAlreadyInCohort = checks.inAnyCohort(item.id, ui.selectedLevel);
                        const isDisabled = isIndividuallySelected || isAlreadyInCohort;
                        const disabledReason = isIndividuallySelected ? 'Selected' : isAlreadyInCohort ? 'In cohort' : '';

                        return (
                            <div
                                key={item.id}
                                className={`city-selection-dropdown__cohort-item ${isInBuilder ? 'city-selection-dropdown__cohort-item--selected' : ''} ${isDisabled ? 'city-selection-dropdown__cohort-item--disabled' : ''}`}
                            >
                                <Checkbox
                                    checked={isInBuilder}
                                    disabled={isDisabled}
                                    onChange={() => !isDisabled && cohort.toggleItem(item.id)}
                                />
                                <span
                                    className={`city-selection-dropdown__cohort-item-label ${isDisabled ? 'city-selection-dropdown__cohort-item-label--disabled' : ''}`}
                                    onClick={() => !isDisabled && cohort.toggleItem(item.id)}
                                >
                                    {item.label}
                                </span>
                                {isDisabled && disabledReason && (
                                    <span className="city-selection-dropdown__cohort-item-badge">{disabledReason}</span>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Create button */}
            <Button
                type="primary"
                block
                disabled={state.cohortBuilder.length < 2}
                onClick={cohort.create}
                className="city-selection-dropdown__cohort-create-btn"
            >
                Create Cohort ({state.cohortBuilder.length} selected)
            </Button>
            {state.cohortBuilder.length === 1 && (
                <div className="city-selection-dropdown__cohort-hint">
                    Select at least 2 items to create a cohort
                </div>
            )}
        </div>
    );

    // Handle mode change
    const handleModeChange = (newMode: BCCSelectionMode) => {
        setters.setMode(newMode);
        setters.setCohortBuilder([]);
    };

    // Handle apply button click
    const handleApply = () => {
        isApplyingRef.current = true;
        handlers.handleDropdownClose(true);
    };

    // Handle dropdown open change
    const handleOpenChange = (open: boolean) => {
        if (open) {
            handlers.handleDropdownOpen();
        } else {
            handlers.handleDropdownClose(isApplyingRef.current);
            isApplyingRef.current = false;
        }
    };

    // Render selection tag
    const renderTag = (selection: BCCCitySelection, closable = true) => (
        <SelectionTag
            key={selection.id}
            selection={selection}
            closable={closable}
            onRemove={handlers.removeSelection}
            citiesList={citiesList}
        />
    );

    // Dropdown content
    const dropdownContent = (
        <div className="city-selection-dropdown__content">
            <ModeToggle currentMode={ui.mode} onModeChange={handleModeChange} />
            {ui.mode === BCCSelectionMode.INDIVIDUAL
                ? renderIndividualMode()
                : renderCohortMode()}
            <Footer
                selections={state.selections}
                onClearAll={handlers.clearAll}
                onApply={handleApply}
                renderTag={renderTag}
            />
        </div>
    );

    return (
        <Dropdown
            open={ui.isOpen}
            onOpenChange={handleOpenChange}
            trigger={['click']}
            placement="bottomLeft"
            dropdownRender={() => dropdownContent}
            overlayStyle={{ zIndex: 9999 }}
            overlayClassName="shadow"
        >
            <div className={`city-selection-dropdown__trigger ${ui.isOpen ? 'city-selection-dropdown__trigger--open' : ''}`}>
                {state.selections.length === 0 ? (
                    <span className="city-selection-dropdown__placeholder">Add Cities</span>
                ) : (
                    <>
                        {state.selections.slice(0, 2).map((sel) => renderTag(sel, false))}
                        {state.selections.length > 2 && (
                            <Tag style={{ margin: 0 }}>+{state.selections.length - 2}</Tag>
                        )}
                    </>
                )}
                <DownOutlined className={`city-selection-dropdown__arrow ${ui.isOpen ? 'city-selection-dropdown__arrow--open' : ''}`} />
            </div>
        </Dropdown>
    );
};